/**
 * Client-side IFC-class → design-discipline mapping for the 3D viewer's
 * discipline filter (WHA-3729).
 *
 * This MIRRORS the pipeline's canonical map — keep the two in sync:
 *   franklin-data-pipeline/pipeline/processors/ifc/discipline.py
 *   → `_CLASS_DISCIPLINE_MAP` (WHA-3574)
 *
 * Values are design-discipline ABBREVIATIONS as stored in
 * `public.design_disciplines.abbreviation` (AR, EK, VN, …) so a bucket joins
 * straight to that table for its localized name + colour. The one exception is
 * the elevator row, which has no abbreviation in the DB and is resolved by
 * NAME ("Lift") — see `LIFT_KEY`.
 *
 * The pipeline deliberately OMITS ambiguous classes (IfcSlab, IfcPipeSegment/
 * Fitting, IfcValve/Pump/Tank/Coil/HeatExchanger, generic IfcFlowSegment /
 * IfcDistribution*) because they straddle disciplines. We keep that omission:
 * any class with no entry here falls into the catch-all `OTHER` bucket so the
 * filter can still ghost/highlight it, without mislabelling it.
 */

import type { IfcSpatialNode } from "@/stores/ifcModel/repository";

/** Catch-all bucket for classes the canonical map can't attribute. */
export const OTHER = "__other__";

/** Elevator discipline key — the DB row carries no abbreviation, so the panel
 * resolves its display name + colour by name instead of abbreviation. */
export const LIFT_KEY = "Lift";

/** IFC entity class → discipline abbreviation (or `LIFT_KEY`). */
export const IFC_CLASS_DISCIPLINE: Readonly<Record<string, string>> = {
  // EK — building structures
  IfcBeam: "EK",
  IfcColumn: "EK",
  IfcFooting: "EK",
  IfcPile: "EK",
  IfcReinforcingBar: "EK",
  IfcReinforcingMesh: "EK",
  IfcReinforcingElement: "EK",
  IfcTendon: "EK",
  IfcTendonAnchor: "EK",
  IfcMember: "EK",
  IfcPlate: "EK",
  // VN — ventilation
  IfcDuctSegment: "VN",
  IfcDuctFitting: "VN",
  IfcDuctSilencer: "VN",
  IfcAirTerminal: "VN",
  IfcAirTerminalBox: "VN",
  IfcFan: "VN",
  IfcAirToAirHeatRecovery: "VN",
  IfcDamper: "VN",
  // KT — heating
  IfcSpaceHeater: "KT",
  IfcBoiler: "KT",
  IfcBurner: "KT",
  // JH — cooling
  IfcChiller: "JH",
  IfcCoolingTower: "JH",
  IfcEvaporativeCooler: "JH",
  // VV — water supply / sanitary
  IfcSanitaryTerminal: "VV",
  IfcWasteTerminal: "VV",
  IfcInterceptor: "VV",
  // EL — power / electrical
  IfcCableSegment: "EL",
  IfcCableCarrierSegment: "EL",
  IfcCableCarrierFitting: "EL",
  IfcCableFitting: "EL",
  IfcLightFixture: "EL",
  IfcOutlet: "EL",
  IfcElectricAppliance: "EL",
  IfcElectricDistributionBoard: "EL",
  IfcElectricDistributionPoint: "EL",
  IfcSwitchingDevice: "EL",
  IfcTransformer: "EL",
  IfcElectricMotor: "EL",
  IfcElectricGenerator: "EL",
  IfcLamp: "EL",
  IfcJunctionBox: "EL",
  // SPR — sprinkler
  IfcFireSuppressionTerminal: "SPR",
  // Lift (resolved by name — the row has no abbreviation)
  IfcTransportElement: LIFT_KEY,
  // AR — architecture
  IfcWall: "AR",
  IfcWallStandardCase: "AR",
  IfcDoor: "AR",
  IfcWindow: "AR",
  IfcStair: "AR",
  IfcStairFlight: "AR",
  IfcRamp: "AR",
  IfcRampFlight: "AR",
  IfcRoof: "AR",
  IfcCurtainWall: "AR",
  IfcCovering: "AR",
  IfcFurnishingElement: "AR",
  IfcFurniture: "AR",
};

/** Spatial-container classes carry no physical mesh — they're excluded from the
 * filter entirely (never bucketed, never counted). Mirrors
 * `SPATIAL_CONTAINER_CLASSES` in IfcSpatialTree.tsx. */
const SPATIAL_CONTAINER_CLASSES: ReadonlySet<string> = new Set([
  "IfcProject",
  "IfcSite",
  "IfcBuilding",
  "IfcBuildingStorey",
  "IfcSpace",
]);

export function isSpatialContainerClass(ifcClass: string): boolean {
  return SPATIAL_CONTAINER_CLASSES.has(ifcClass);
}

/** Bucket an IFC class into a discipline key, or `OTHER` when the canonical map
 * doesn't attribute it. Returns null for spatial containers (no mesh). */
export function disciplineKeyForClass(ifcClass: string): string | null {
  if (isSpatialContainerClass(ifcClass)) return null;
  return IFC_CLASS_DISCIPLINE[ifcClass] ?? OTHER;
}

/** Human-readable element label from an IFC class — strips the `Ifc` prefix and
 * spaces out CamelCase ("IfcDuctSegment" → "Duct Segment"). Matches the
 * class-label convention used across the viewer's panels. */
export function classLabel(ifcClass: string): string {
  return ifcClass.replace(/^Ifc/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

/** discipline key → the element labels the canonical map attributes to it,
 * de-duplicated + sorted. Built once from IFC_CLASS_DISCIPLINE. `OTHER` has no
 * fixed membership (it's the catch-all), so it isn't present here. */
const DISCIPLINE_CLASS_LABELS: Readonly<Record<string, readonly string[]>> = (() => {
  const byKey = new Map<string, Set<string>>();
  for (const [ifcClass, key] of Object.entries(IFC_CLASS_DISCIPLINE)) {
    const set = byKey.get(key) ?? new Set<string>();
    set.add(classLabel(ifcClass));
    byKey.set(key, set);
  }
  const out: Record<string, string[]> = {};
  for (const [key, labels] of byKey) out[key] = [...labels].sort((a, b) => a.localeCompare(b));
  return out;
})();

/** The element-type labels a discipline covers (e.g. AR → ["Covering", "Curtain
 * Wall", "Door", …]). Empty for `OTHER`/unknown keys, which have no fixed set. */
export function disciplineIncludedClassLabels(key: string): readonly string[] {
  return DISCIPLINE_CLASS_LABELS[key] ?? [];
}

/**
 * Per-discipline display metadata. Self-contained (not read from
 * `public.design_disciplines`) because that table's colour columns aren't
 * surfaced in the frontend model and the filter is a customer-facing surface
 * that shouldn't depend on the admin reference store. Names mirror
 * `design_disciplines` (name_en / name Estonian); `OTHER` is the catch-all.
 *
 * `color` is the swatch + 3D highlight tint — a distinct, dark-panel-readable
 * hue per discipline.
 */
export interface DisciplineMeta {
  en: string;
  et: string;
  /** "#RRGGBB" — swatch background and tint colour. */
  color: string;
}

export const DISCIPLINE_META: Readonly<Record<string, DisciplineMeta>> = {
  AR: { en: "Architecture", et: "Arhitektuur", color: "#4f8ff7" },
  EK: { en: "Building Structures", et: "Ehituskonstruktsioonid", color: "#f59e0b" },
  VN: { en: "Ventilation", et: "Ventilatsioon", color: "#22d3ee" },
  KT: { en: "Heating", et: "Küte", color: "#ef4444" },
  JH: { en: "Cooling", et: "Jahutus", color: "#38bdf8" },
  VV: { en: "Water Supply", et: "Veevarustus", color: "#6366f1" },
  EL: { en: "Power / Electrical", et: "Tugevvool", color: "#eab308" },
  SPR: { en: "Sprinkler", et: "Sprinkler", color: "#ec4899" },
  [LIFT_KEY]: { en: "Elevator", et: "Lift", color: "#a855f7" },
  [OTHER]: { en: "Other / Unclassified", et: "Muu / määramata", color: "#94a3b8" },
};

/** Stable display order for the panel rows; `OTHER` always last. */
export const DISCIPLINE_ORDER: readonly string[] = [
  "AR",
  "EK",
  "VN",
  "KT",
  "JH",
  "VV",
  "EL",
  "SPR",
  LIFT_KEY,
  OTHER,
];

/** Localized discipline label, falling back to the key when unknown. */
export function disciplineLabel(key: string, language: "en" | "et"): string {
  const meta = DISCIPLINE_META[key];
  if (!meta) return key;
  return language === "et" ? meta.et : meta.en;
}

/** Discipline swatch / tint colour as "#RRGGBB" (falls back to OTHER's grey). */
export function disciplineColor(key: string): string {
  return (DISCIPLINE_META[key] ?? DISCIPLINE_META[OTHER]).color;
}

/** Parse a "#RRGGBB" colour to a three.js-friendly 0xRRGGBB number. */
export function hexToInt(hex: string): number {
  return parseInt(hex.replace(/^#/, ""), 16);
}

/** One discipline present in a loaded model: its element count and the IFC
 * classes (present in THIS model) that fall under it. */
export interface DisciplineBucket {
  key: string;
  count: number;
  /** IFC classes in this model bucketed here — drives the row tooltip and the
   * GUID resolution for ghost/tint. Sorted for stable display. */
  classes: string[];
}

/**
 * Bucket a model's elements into disciplines from the spatial tree's
 * `element_class_counts` (only storey nodes carry it). Returns buckets in
 * `DISCIPLINE_ORDER`, omitting disciplines with no elements in this model.
 * Shared by the discipline panel (counts + tooltip) and the canvas styling
 * effect (key → classes → GUIDs).
 */
export function bucketModelDisciplines(nodes: IfcSpatialNode[]): DisciplineBucket[] {
  const byKey = new Map<string, { count: number; classes: Set<string> }>();
  for (const node of nodes) {
    const counts = node.element_class_counts;
    if (!counts) continue;
    for (const [ifcClass, n] of Object.entries(counts)) {
      const key = disciplineKeyForClass(ifcClass);
      if (key == null) continue; // spatial container — no mesh
      const entry = byKey.get(key) ?? { count: 0, classes: new Set<string>() };
      entry.count += n ?? 0;
      entry.classes.add(ifcClass);
      byKey.set(key, entry);
    }
  }
  const out: DisciplineBucket[] = [];
  for (const key of DISCIPLINE_ORDER) {
    const entry = byKey.get(key);
    if (entry && entry.count > 0) {
      out.push({ key, count: entry.count, classes: [...entry.classes].sort() });
    }
  }
  return out;
}
